import type { Universe } from '@/types/universe';
import type { AddRevealRef } from '@/hooks/useScrollReveal';

interface HowItWorksProps {
  universe: Universe;
  addRevealRef: AddRevealRef;
}

const serif = "'DM Serif Display', Georgia, serif";

export default function HowItWorks({ universe, addRevealRef }: HowItWorksProps) {
  return (
    <section id="how-it-works" className="bg-background py-20 md:py-28 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-16">
          <p className="flex items-center gap-3 text-accent text-[11px] font-semibold uppercase tracking-widest mb-4">
            <span className="inline-block w-6 h-px bg-accent" />
            La méthode
          </p>
          <h2
            ref={addRevealRef}
            className="reveal text-primary text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-none"
            style={{ fontFamily: serif, fontWeight: 400 }}>
            {universe.stepsTitle}<br />
            <span className="italic opacity-60">{universe.stepsTitleAccent}</span>
          </h2>
        </div>

        {/* Split layout */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Steps */}
          <div className="space-y-0">
            {universe.steps.map((step, i) =>
              <div
                key={i}
                ref={addRevealRef}
                className="reveal reveal-delay-1 group border-t border-primary/[0.08] py-10 cursor-default">
                <div className="flex gap-8 items-start">
                  <span
                    className="shrink-0 text-5xl font-light italic text-accent opacity-30 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ fontFamily: serif }}>
                    {step.num}
                  </span>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-primary uppercase tracking-wide mb-3">
                      {step.title}
                    </h3>
                    <div className="h-px mb-4 bg-primary/10 group-hover:opacity-40 transition-opacity duration-500" />
                    <p className="text-muted text-sm leading-relaxed font-light">{step.desc}</p>
                    <p className="mt-3 text-accent text-[11px] uppercase tracking-widest font-semibold">{step.badge}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="border-t border-primary/[0.08]" />
          </div>

          {/* Right visual : image + cartes flottantes */}
          <div ref={addRevealRef} className="reveal reveal-delay-2 relative">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5]">
              <img
                src={universe.stepImage}
                alt={universe.stepImageAlt}
                className="absolute inset-0 w-full h-full object-cover" />
              <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(to top, rgba(26,26,26,0.55) 0%, transparent 50%)' }} />
            </div>

            {/* Carte stat flottante */}
            <div className="absolute -bottom-6 -left-6 md:-left-10 rounded-2xl p-6 shadow-2xl bg-accent text-primary min-w-[200px]">
              <p className="text-4xl font-semibold tracking-tight" style={{ fontFamily: serif }}>15 min</p>
              <p className="mt-1 text-[11px] uppercase tracking-widest font-semibold opacity-70">pour maîtriser le guide</p>
            </div>

            {/* Badge flottant */}
            <div className="absolute -top-4 -right-4 md:-right-6 rounded-2xl p-5 shadow-xl bg-primary text-white">
              <p className="text-2xl font-semibold tracking-tight" style={{ fontFamily: serif }}>100%</p>
              <p className="mt-0.5 text-[10px] uppercase tracking-widest font-semibold opacity-50">de réussite</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}